import { useState } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { ReactComponent as ListedSaleIcon } from '../assets/dashboard/listed-for-sale.svg'
import { ReactComponent as ListingRemovedIcon } from '../assets/dashboard/listing-removed.svg'
import { ReactComponent as SoldIcon } from '../assets/dashboard/sold.svg'
import { ReactComponent as ListedRentIcon } from '../assets/dashboard/listed-for-rent.svg'
import { ReactComponent as IncreaseIcon } from '../assets/dashboard/price-increased.svg'
import { ReactComponent as DecreaseIcon } from '../assets/dashboard/price-decreased.svg'
import GalleryModal from '../components/GalleryModal'
import AppNavbar from '../features/AppNavbar'
import PageShell from '../layouts/PageShell'

// ─── Color scale from assets/arv/school-ratings-color.svg ────────────────────
// 0=gray · 1–4=mid-green · 5–7=dark-green · 8–10=brand-dark
const getSchoolRatingColor = (score) => {
  const n = parseInt(score, 10)
  if (!n || n <= 0) return '#C4CCBE'
  if (n <= 4)       return '#4F7A35'
  if (n <= 7)       return '#2F4A1F'
  return '#17270D'
}

const STATUS_ICON_MAP = {
  'Listed':          ListedSaleIcon,
  'Listing removed': ListingRemovedIcon,
  'Sold':            SoldIcon,
  'Listed for rent': ListedRentIcon,
  'price_increase':  IncreaseIcon,
  'price_decrease':  DecreaseIcon,
}

const STATUS_LABEL_MAP = {
  'Listed':          'Listed for Sale',
  'Listing removed': 'Listing Removed',
  'Sold':            'Sold',
  'Listed for rent': 'Listed for Rent',
  'price_increase':  'Price Increased',
  'price_decrease':  'Price Decreased',
}

const STATUS_BADGE_BG = {
  'Listed':          '#D85263',
  'Listed for rent': '#F5C312',
}

const formatPrice = (n) => {
  if (n == null) return ''
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n)
}

const formatDate = (iso) => {
  if (!iso) return ''
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

const sectionLabelCls = 'text-[14px] font-bold text-brand-secondary mb-[12px]'
const fieldLabelCls   = 'block text-[11px] text-brand-secondary uppercase tracking-wide mb-[4px]'
const fieldValueCls   = 'text-[13px] text-brand-dark font-semibold'

export default function ListingDetails() {
  const { id } = useParams()
  const navigate = useNavigate()
  const location = useLocation()

  const { comp, filteredComps = [], compIndex = 0 } = location.state ?? {}

  const [galleryPhotos, setGalleryPhotos] = useState(null)
  const [descExpanded,  setDescExpanded]  = useState(false)

  if (!comp) {
    return (
      <PageShell>
        <AppNavbar />
        <div className=" mt-[40px] flex items-center gap-x-[8px]">
          <p className="text-[14px] text-brand-secondary">Listing data not available.</p>
          <button
            onClick={() => navigate(`/property/${id}/arv`)}
            className="text-[14px] text-brand-green underline hover:opacity-70 transition-opacity"
          >
            Return to ARV Analysis
          </button>
        </div>
      </PageShell>
    )
  }

  const { street, city, state: stateCode, zip } = comp.address ?? {}
  const addressDisplay = [street, city, stateCode, zip].filter(Boolean).join(', ')
  const { beds, baths, sqft } = comp.specs ?? {}
  const photos = comp.photos ?? []

  const goPrev = () => {
    if (compIndex <= 0) return
    const idx = compIndex - 1
    navigate(`/property/${id}/arv/listing/${idx}`, {
      state: { comp: filteredComps[idx], filteredComps, compIndex: idx },
      replace: true,
    })
  }

  const goNext = () => {
    if (compIndex >= filteredComps.length - 1) return
    const idx = compIndex + 1
    navigate(`/property/${id}/arv/listing/${idx}`, {
      state: { comp: filteredComps[idx], filteredComps, compIndex: idx },
      replace: true,
    })
  }

  const openGallery = () => { if (photos.length > 0) setGalleryPhotos(photos) }

  const statusLower  = comp.status?.toLowerCase() ?? ''
  const statusColor  = statusLower === 'sold' ? '#4CBC18' : statusLower === 'active' ? '#C13A4A' : '#F5C312'

  const sortedHistory = [...(comp.history ?? [])].sort((a, b) => new Date(b.date) - new Date(a.date))

  const hoaArray    = comp.optionalDetails?.homeowners_association ?? []
  const hoaFeeEntry  = hoaArray.find(s => s.startsWith('Association Fee:'))
  const hoaFreqEntry = hoaArray.find(s => s.startsWith('Association Fee Frequency:'))
  const hoaFeeAmt    = hoaFeeEntry  ? Number(hoaFeeEntry.split(':')[1]?.trim())  : null
  const hoaFreq      = hoaFreqEntry ? hoaFreqEntry.split(':')[1]?.trim()         : null
  const hoaAmenities = comp.optionalDetails?.amenities_and_community_features?.[0] ?? null
  const hasHoa       = (hoaFeeAmt != null && hoaFeeAmt > 0) || hoaAmenities != null

  const hasSchools = comp.elementary_school || comp.middle_school || comp.high_school
  const hasTaxes   = comp.total_tax || comp.assessment_year
  const isMac = navigator.userAgent.includes('Mac');
  const containerStyle = {
    paddingRight: isMac ? '24px' : '7px',
  };

  return (
    <PageShell>
      <AppNavbar />
      <div className=" mt-[24px] flex flex-col gap-y-[20px] pb-[60px]">

        {/* Header */}
        <div className="flex items-center justify-between h-[40px]">
          <button
            onClick={() => navigate(`/property/${id}/arv`)}
            className="text-[13px] font-semibold text-brand-green hover:opacity-70 transition-opacity"
          >
            ← Back
          </button>
          {filteredComps.length > 1 && (
            <div className="flex items-center gap-x-[8px] font-semibold">
              <button
                onClick={goPrev}
                disabled={compIndex <= 0}
                className="px-[16px] h-[36px] rounded-[24px] text-[13px] text-white bg-brand-dark hover:bg-[#21450F] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                ← Previous
              </button>
              <span className="text-[13px] text-brand-secondary px-[8px]">
                {compIndex + 1} / {filteredComps.length}
              </span>
              <button
                onClick={goNext}
                disabled={compIndex >= filteredComps.length - 1}
                className="px-[16px] h-[36px] rounded-[24px] text-[13px] text-white bg-brand-dark hover:bg-[#21450F] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Next →
              </button>
            </div>
          )}
        </div>

        {/* Gallery card — scrollable to see all photos */}
        <div className="overflow-y-auto bg-white border border-[#DCEBD4] rounded-2xl p-[24px] relative scrollbar-gutter-stable" style={containerStyle}>
          <div
            className="grid grid-cols-3 gap-[8px]"
            style={{ maxHeight: 490 }}
          >
            {photos.length > 0 ? photos.map((photo, i) => (
              <div
                key={i}
                onClick={openGallery}
                className="rounded-[8px] overflow-hidden bg-brand-grayGreen cursor-pointer hover:opacity-90 transition-opacity shrink-0"
                style={{ aspectRatio: '3/2' }}
              >
                <img src={photo.href} alt={`Photo ${i + 1}`} className="w-full h-full object-cover" />
              </div>
            )) : (
              // Placeholder grid when no photos
              Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="rounded-[8px] bg-brand-grayGreen shrink-0" style={{ aspectRatio: '3/2' }} />
              ))
            )}
          </div>
          {photos.length > 0 && (
            <button
              onClick={openGallery}
              className="absolute top-[38px] right-[38px] px-[12px] h-[32px] bg-black/30 rounded-[16px] text-[13px] font-semibold text-white hover:bg-black/60 transition-colors "
            >
              View Gallery
            </button>
          )}
        </div>

        {/* Body: 2 columns */}
        <div className="flex gap-x-[16px] items-start">

          {/* ── Left card: Metrics + Description + Property Details (ONE card) ── */}
          <div className="flex-1 min-w-0 bg-white border border-[#DCEBD4] rounded-2xl p-[28px] flex flex-col gap-y-[28px]">

            {/* Metrics bar */}
            <div className='flex justify-between'>
              <div>
                <div className="flex items-center gap-x-[10px]">
                  <span
                    className="shrink-0 px-[4px] py-[2px] rounded-[4px] text-[14px] text-white"
                    style={{ background: statusColor }}
                  >
                    {(comp.status ?? 'Unknown').toUpperCase()}
                  </span>
                  <span className="text-[26px] font-black text-brand-dark leading-tight">
                    { comp.status !== 'Sold' ? formatPrice(comp.price) : formatPrice(comp.sold_price) }
                  </span>
                </div>
                <p className="text-[16px] text-brand-secondary mt-[4px]">{addressDisplay}</p>
              </div>
              {/* Specs row with dividers */}
              <div className="flex items-center ">
                {[
                  { label: 'Beds',      value: beds ?? '' },
                  { label: 'Baths',     value: baths ?? '' },
                  { label: 'Sq. Ft.',   value: sqft ? Number(sqft).toLocaleString() : '' },
                  { label: 'Yr. Built', value: comp.year_built ?? '' },
                ].map(({ label, value }, i) => (
                  <div key={label} className="flex items-center">
                    {i > 0 && <div className="w-px h-[36px] bg-brand-fieldBdr mx-[20px] shrink-0" />}
                    <div className="flex flex-col gap-1 items-start">
                      <span className="text-[18px] font-black text-brand-dark leading-tight">{value}</span>
                      <span className="text-[16px] text-brand-disabled">{label}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Description with Read more */}
            {comp.descriptionText && (
              <div>
                <p className="text-[16px] font-extrabold text-brand-dark mb-[12px]">Description</p>
                <p className={`text-[13px] text-brand-dark leading-relaxed ${descExpanded ? '' : 'line-clamp-3'}`}>
                  {comp.descriptionText}
                </p>
                <button
                  onClick={() => setDescExpanded(e => !e)}
                  className="flex items-center gap-x-[4px] text-[13px] font-medium text-brand-green mt-[8px] hover:opacity-70 transition-opacity"
                >
                  <span>{descExpanded ? '↑ Read less' : '↓ Read more'}</span>
                </button>
              </div>
            )}

            {/* Property Details */}
            <div className="flex flex-col gap-y-[24px]">
              <p className="text-[16px] font-extrabold text-brand-dark">Property Details</p>

              {/* Features */}
              <div>
                <p className={sectionLabelCls}>Features</p>
                <div className="grid grid-cols-3 gap-x-[24px] gap-y-[20px]">
                  <div>
                    <span className={fieldLabelCls}>Property Type</span>
                    <p className={fieldValueCls}>{comp.property_type ?? 'Single Family'}</p>
                  </div>
                  <div>
                    <span className={fieldLabelCls}>Lot Size</span>
                    <p className={fieldValueCls}>
                      {comp.lot_size ? `${Number(comp.lot_size).toLocaleString()} Acres` : ''}
                    </p>
                  </div>
                  <div>
                    <span className={fieldLabelCls}>DOM</span>
                    <p className={fieldValueCls}>{comp.dom ?? ''}</p>
                  </div>
                  <div>
                    <span className={fieldLabelCls}>Flood Zone</span>
                    <p className={fieldValueCls}>{comp.flood_zone ?? ''}</p>
                  </div>
                  <div>
                    <span className={fieldLabelCls}>Parking</span>
                    <p className={fieldValueCls}>
                      {comp.parking ?? ''}
                      {comp.parking_spaces ? ` (${comp.parking_spaces} spaces)` : ''}
                    </p>
                  </div>
                </div>
              </div>

              {/* HOA */}
              <div>
                <p className={sectionLabelCls}>HOA</p>
                <div className="grid grid-cols-3 gap-x-[24px] gap-y-[20px]">
                  <div>
                    <span className={fieldLabelCls}>HOA Fee</span>
                    <p className={fieldValueCls}>
                      {hoaFeeAmt != null && hoaFeeAmt > 0
                        ? `${formatPrice(hoaFeeAmt)}${hoaFreq ? `/${hoaFreq}` : ''}`
                        : ''}
                    </p>
                  </div>
                  <div className="col-span-2">
                    <span className={fieldLabelCls}>Amenities</span>
                    <p className={fieldValueCls}>{hoaAmenities}</p>
                  </div>
                </div>
              </div>

              {/* Schools */}
              {hasSchools && (
                <div>
                  <p className={sectionLabelCls}>Schools</p>
                  <div className="grid grid-cols-3 gap-x-[16px]">
                    {[
                      { level: 'Elementary School', name: comp.elementary_school, score: comp.elementary_school_score },
                      { level: 'Middle School',     name: comp.middle_school,     score: comp.middle_school_score },
                      { level: 'High School',       name: comp.high_school,       score: comp.high_school_score },
                    ].map(({ level, name, score }) => (
                      <div key={level}>
                        <span className={fieldLabelCls}>{level}</span>
                        <div className="flex items-center gap-x-[8px] mt-[6px]">
                          {score != null && (
                            <span
                              className="w-[44px] h-[44px] rounded-full flex items-center justify-center text-white font-bold shrink-0"
                              style={{ backgroundColor: getSchoolRatingColor(score) }}
                            >
                              <span style={{ fontSize: 15 }}>{score}</span>
                              <span style={{ fontSize: 10, fontWeight: 400 }}>/10</span>
                            </span>
                          )}
                          <p className={fieldValueCls}>{name || ''}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Tax & Assessment */}
              {hasTaxes && (
                <div>
                  <p className={sectionLabelCls}>Taxes </p>
                  <div className="grid grid-cols-3 gap-x-[24px] gap-y-[20px]">
                    <div>
                      <span className={fieldLabelCls}>Tax Year</span>
                      <p className={fieldValueCls}>{comp.assessment_year ?? ''}</p>
                    </div>
                    <div>
                      <span className={fieldLabelCls}>Total Tax</span>
                      <p className={fieldValueCls}>{formatPrice(comp.total_tax)}</p>
                    </div>
                    
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* ── Right card: Recent History ── */}
          <div className="w-[450px] h-[452px] shrink-0 bg-white border border-[#DCEBD4] rounded-2xl p-[28px] overflow-y-auto">
            <p className="text-[16px] font-extrabold text-brand-dark mb-[16px]">Recent History</p>

            {/* Table header */}
            <div className="flex gap-[8px] py-[4px] bg-white rounded-t-[4px]">
              <div className="w-[110px] h-[36px] flex items-center shrink-0">
                <span className="text-[13px] font-bold text-brand-dark">Date</span>
              </div>
              <div className="flex-1 h-[36px] flex items-center">
                <span className="text-[13px] font-bold text-brand-dark">Status</span>
              </div>
              <div className="w-[80px] h-[36px] flex items-center  pr-[12px] shrink-0">
                <span className="text-[13px] font-bold text-brand-dark">Price</span>
              </div>
            </div>
            <div style={{ maxHeight: 320 }}>
              {sortedHistory.length === 0 ? (
                <p className="text-[13px] text-brand-secondary py-[16px] pl-[12px]">No history available.</p>
              ) : (
                sortedHistory.map((entry, i) => {
                  const Icon    = STATUS_ICON_MAP[entry.event_name]
                  const badgeBg = STATUS_BADGE_BG[entry.event_name]
                  return (
                    <div key={i} className="flex gap-[8px] items-center h-[52px] border-b border-brand-rowBorder">
                      <div className="w-[110px] shrink-0">
                        <span className="text-[13px] text-brand-dark">{formatDate(entry.date)}</span>
                      </div>
                      <div className="flex-1 flex items-center gap-[8px] min-w-0">
                        {Icon && (
                          badgeBg ? (
                            <div
                              className="size-[20px] rounded-full flex items-center justify-center shrink-0"
                              style={{ background: badgeBg }}
                            >
                              <Icon />
                            </div>
                          ) : (
                            <Icon className="size-[20px] shrink-0" />
                          )
                        )}
                        <span className="text-[13px] text-brand-dark truncate">
                          {STATUS_LABEL_MAP[entry.event_name] ?? entry.event_name ?? ''}
                        </span>
                      </div>
                      <div className="w-[80px] pr-[12px] shrink-0 ">
                        <span className="text-[13px] text-brand-dark">
                          {entry.price ? formatPrice(entry.price) : ''}
                        </span>
                      </div>
                    </div>
                  )
                })
              )}
            </div>
          </div>
        </div>
      </div>

      {galleryPhotos && (
        <GalleryModal photos={galleryPhotos} onClose={() => setGalleryPhotos(null)} />
      )}
    </PageShell>
  )
}
